import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  SelectItem,
  Stack,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useSectionSave,
  useSectionsDelete,
  useTabsByEntityType,
} from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiSection,
  ApiCFEntityTypes,
  ApiSectionTypes,
  ApiCFTabType,
} from "@/types/custom-field.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface CustomSectionEditModalProps extends ModalProps {
  tabType: ApiCFTabType;
  section?: ApiSection | undefined;
  entityType?: ApiCFEntityTypes;
}

export function CustomSectionEditModal({
  tabType,
  section,
  entityType = section?.entity_type ?? ApiCFEntityTypes.employee,
  ...props
}: CustomSectionEditModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { showInfo } = useToasts();
  const { close, openModal } = useModals();
  const { getTabTitle } = useCustomFieldsHook();
  const { mutateAsync: deleteSection } = useSectionsDelete();
  const { data: tabs } = useTabsByEntityType(entityType);
  const isEmployee = entityType === ApiCFEntityTypes.employee;
  const tabOptions =
    tabs?.map(
      (tab) =>
        ({
          id: tab.type,
          title: getTabTitle(tab),
        }) as SelectItem
    ) ?? [];
  const sectionTabType = tabs?.find((tab) => tab.id === section?.tab_id)?.type ?? tabType;

  const CustomSectionSchemaObject = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    tab_type: z.enum(ApiCFTabType, { error: t("errors:field_required") }),
  });

  type CustomSectionSchema = z.infer<typeof CustomSectionSchemaObject>;

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<CustomSectionSchema>({
    mode: "onSubmit",
    resolver: zodResolver(CustomSectionSchemaObject),
    defaultValues: {
      title: section?.title ?? "",
      tab_type: sectionTabType,
    },
  });

  useEffect(() => {
    reset({
      title: section?.title ?? "",
      tab_type: sectionTabType,
    });
  }, [section, sectionTabType, reset]);

  const sectionSave = useSectionSave(control);

  const onSubmit = handleSubmit(async (data) => {
    const result = await sectionSave.mutateAsync({
      id: section?.id,
      payload: {
        tab_type: data.tab_type,
        title: data.title,
        entity_type: entityType,
      },
    });

    if (!section) {
      analyticsService.trackEvent(analyticEvents.customFields.sectionCreated, {
        [analyticProperties.id]: result.id,
      });
    } else {
      analyticsService.trackEvent(analyticEvents.customFields.sectionEdited, {
        [analyticProperties.id]: result.id,
      });
    }

    if (result) close(id);
  });

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (section?.section_type != ApiSectionTypes.custom) return;

        try {
          const result = await deleteSection(section.id);

          if (result) showInfo({ title: t("custom_fields:section_deleted") });

          analyticsService.trackEvent(analyticEvents.customFields.sectionDeleted, {
            [analyticProperties.id]: section.id,
          });
        } catch {
          return;
        }

        close(id);
      },
    });
  };

  return (
    <Modal
      {...props}
      title={section ? t("custom_fields:edit_section") : t("custom_fields:new_section")}
      actions={
        <Stack gap={"md"}>
          <ServerErrorField errors={errors} />
          <Button
            variant={"primary"}
            isLoading={sectionSave.isPending}
            size={"lg"}
            onClick={onSubmit}
          >
            {section ? t("common:save") : t("common:create")}
          </Button>
        </Stack>
      }
      withCloser={true}
      closeByEscEnabled={true}
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            label={t("common:title")}
            type={"text"}
            {...register("title")}
            error={errors?.title?.message ?? ""}
          />
          {isEmployee && (
            <Controller
              render={({ field: { onChange, value } }) => (
                <Select
                  emptyTitle={t("common:section")}
                  onChange={onChange}
                  value={value}
                  options={tabOptions}
                  required
                  error={errors.tab_type?.message}
                />
              )}
              name={"tab_type"}
              control={control}
            />
          )}
          {section && section.section_type == ApiSectionTypes.custom ? (
            <DeleteListItem
              onDelete={handleDelete}
              title={t("custom_fields:confirmation_delete_section")}
            />
          ) : (
            <></>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
